import { z } from 'zod';

import { CommonToolNames } from './commonToolContract';
import {
  FindDocumentsToolName,
  GetDocumentOverviewToolName,
} from './documentDiscoveryContract';
import { DocumentSearchToolName } from './documentSearchContract';
import { RecordAnswerSourcesToolName } from './evidenceContract';
import { FetchUrlToolName } from './fetchUrlContract';
import { SearchPdfToolName } from './searchPdfContract';
import { SeeImageToolName } from './seeImageContract';
import {
  RetiredShopProductToolNames,
  ShopProductToolNames,
} from './shopAssistant/shopProductToolsContract';
import { ViewPageToolName } from './viewPageContract';
import { EndCallToolName, TransferCallToolName } from './voiceToolContract';
import { WebSearchToolName } from './webSearchContract';
import { UpdateWorkingMemoryToolName } from './workingMemoryContract';
import { Agent } from '../../enums';

export * from './shopAssistant/shopProductToolsContract';
export * from './documentSearchContract';
export * from './searchPdfContract';
export * from './documentDiscoveryContract';
export * from './commonToolContract';
export * from './webSearchContract';
export * from './fetchUrlContract';
export * from './seeImageContract';
export * from './viewPageContract';
export * from './workingMemoryContract';
export * from './voiceToolContract';
export * from './builtinToolCatalog';
export * from './mcpConnectorCatalog';

export const ToolNames = {
  ...ShopProductToolNames,
  ...CommonToolNames,
  DocumentSearch: DocumentSearchToolName,
  FindDocuments: FindDocumentsToolName,
  GetDocumentOverview: GetDocumentOverviewToolName,
  WebSearch: WebSearchToolName,
  FetchUrl: FetchUrlToolName,
  SearchPdf: SearchPdfToolName,
  SeeImage: SeeImageToolName,
  ViewPage: ViewPageToolName,
  UpdateWorkingMemory: UpdateWorkingMemoryToolName,
  TransferCall: TransferCallToolName,
  EndCall: EndCallToolName,
  RecordAnswerSources: RecordAnswerSourcesToolName,
} as const;

export type ToolName = (typeof ToolNames)[keyof typeof ToolNames];

export const BuiltinToolNames = [
  ToolNames.DocumentSearch,
  ToolNames.FindDocuments,
  ToolNames.GetDocumentOverview,
  ToolNames.WebSearch,
  ToolNames.FetchUrl,
  ToolNames.SearchPdf,
  ToolNames.SeeImage,
  ToolNames.ViewPage,
  ToolNames.GetOperator,
  ToolNames.GetShopProducts,
  ToolNames.GetSimilarProducts,
  ToolNames.RecommendShopProducts,
  ToolNames.ShowShopProductDetail,
  ToolNames.GetShopProductDetails,
  ToolNames.GetShopCategories,
  ToolNames.GetShopDeliveryOptions,
  ToolNames.UpdateWorkingMemory,
  ToolNames.TransferCall,
  ToolNames.EndCall,
  ToolNames.RecordAnswerSources,
] as const;

export const builtinToolNameSchema = z.enum(BuiltinToolNames);

export type BuiltinToolName = z.infer<typeof builtinToolNameSchema>;

const retiredBuiltinToolNames: ReadonlySet<string> = new Set(
  RetiredShopProductToolNames,
);

/**
 * Brings a stored or submitted allowlist up to the current built-ins.
 *
 * A retired detail lookup becomes `getShopProductDetails`, so a chat that
 * could look products up before still can; any other name that is not a
 * built-in is dropped. Done here rather than by a data migration because
 * migrations deploy before the applications, and the previous release would
 * not recognise the replacement name in a rewritten row.
 */
export function normalizeBuiltinToolNames(
  names: readonly unknown[],
): BuiltinToolName[] {
  const known = new Set<string>(BuiltinToolNames);
  const normalized = new Set<BuiltinToolName>();

  for (const name of names) {
    if (typeof name !== 'string') continue;

    if (retiredBuiltinToolNames.has(name)) {
      normalized.add(ToolNames.GetShopProductDetails);
    } else if (known.has(name)) {
      normalized.add(name as BuiltinToolName);
    }
  }

  return [...normalized];
}

/** Allowlist as chats store it: validated after normalization. */
export const builtinToolAllowlistSchema = z
  .array(z.string())
  .transform(names => normalizeBuiltinToolNames(names));

/**
 * Built-ins a new chat never gets for free — the admin turns them on per chat.
 *
 * Web search has its own dedicated toggle (`hasWebSearch`), so prefilling it
 * would only be misleading. The two document-discovery tools are excluded for
 * a different reason: they trade one extra tool round-trip for orientation
 * over the corpus, which pays off on a large knowledge base and is pure
 * latency on a small one. Corpus size is not knowable from the agent type, so
 * the decision belongs to whoever can see the knowledge base — not to a
 * default.
 */
const OPT_IN_BUILTIN_TOOL_NAMES: ReadonlySet<BuiltinToolName> =
  new Set<BuiltinToolName>([
    ToolNames.WebSearch,
    ToolNames.FindDocuments,
    ToolNames.GetDocumentOverview,
  ]);

/**
 * Built-ins that no `Agent` persona gets by default because they only exist
 * on a non-chat surface — the surface filter in `resolveToolsForChat` would
 * drop them anyway, but they should not clutter a chat's allowlist either.
 */
const SURFACE_ONLY_TOOLS: ReadonlySet<BuiltinToolName> = new Set([
  ToolNames.TransferCall,
  ToolNames.EndCall,
  ToolNames.RecordAnswerSources,
]);

export function getDefaultBuiltinToolAllowlistForAgent(
  agent: Agent,
): BuiltinToolName[] {
  if (agent === Agent.ShopAssistant) {
    return BuiltinToolNames.filter(
      name =>
        !OPT_IN_BUILTIN_TOOL_NAMES.has(name) && !SURFACE_ONLY_TOOLS.has(name),
    );
  }

  // Conversational AND any unrecognized/legacy value (defense in depth — an
  // un-remapped agent must still get documentSearch + operator, never a
  // tool-less chat). Working memory is on by default; the admin can opt out.
  return [
    ToolNames.GetOperator,
    ToolNames.DocumentSearch,
    ToolNames.UpdateWorkingMemory,
    ToolNames.FetchUrl,
    ToolNames.SearchPdf,
    ToolNames.SeeImage,
    ToolNames.ViewPage,
  ];
}

export * from './evidenceContract';
export * from './conversationHistoryContract';
